import { useEffect, useMemo, useState } from 'react';
import Typography from '@components/common/typography';
import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import Switch from '@components/common/switch';
import { useForm } from '@hooks/helpers/useForm';
import FormField from '@components/form_field';
import { FormProvider } from 'react-hook-form';
import Tabs from '@components/tabs';
import Checkbox from '@components/common/checkbox';
import { useUserContext } from '@hooks/context/useUserContext';
import Button from '@components/common/button';
import { useLocales } from '@hooks/helpers/useLocales';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { apiUserService } from '@api';
import { TNotificationConfig, TNotificationSettings } from '@xeppt/xeppt-sdk/types/user';
import { routes } from '@const/routes';
import { useNavigationContext } from '@hooks/context/useNavigationContext';
import { getVisibleNotificationGroups, humanizeNotificationGroupKey } from '@utils/notifications';
import { ENotificationCategory } from '@xeppt/xeppt-sdk/types';
import { Icon } from '@components/icons';

const tabs = [
    {
        label: 'card',
        value: 'card',
        before: <Icon name="credit_card" />
    },
    {
        label: 'personal',
        value: 'personal',
        before: <Icon name="person" />
    },
    {
        label: 'payments',
        value: 'payments',
        before: <Icon name="money" />
    },
    {
        label: 'schedule',
        value: 'schedule',
        before: <Icon name="schedule" />
    },
    {
        label: 'marketing',
        value: 'marketing',
        before: <Icon name="marketing" />
    },
    {
        label: 'loyalty',
        value: 'loyalty',
        before: <Icon name="loyalty" />
    }
];

const SettingsNotificationsLayout = () => {
    const { t } = useTranslation('settings', {
        keyPrefix: 'notifications'
    });
    const { user, refetchUser } = useUserContext();
    const { requestSuccessLocale, requestErrorLocale } = useLocales();
    const formMethods = useForm({});
    const [activeTab, setActiveTab] = useState<ENotificationCategory>(ENotificationCategory.CARD);
    const { handleBack } = useNavigationContext();
    const { submitLocale } = useLocales();
    const [isDisabled, setIsDisabled] = useState(false);

    const activeTabNotifications = useMemo(() => {
        return getVisibleNotificationGroups(activeTab, user?.notifications);
    }, [user, activeTab]);

    const isEmailActive = useMemo(() => {
        const values = formMethods.getValues();
        return !!activeTabNotifications.find((item) => values?.[item]?.['e']);
    }, [user, activeTabNotifications, formMethods.watch()]);

    const isSmsActive = useMemo(() => {
        const values = formMethods.getValues();
        return !!activeTabNotifications.find((item) => values?.[item]?.['s']);
    }, [user, activeTabNotifications, formMethods.watch()]);

    const isPushActive = useMemo(() => {
        const values = formMethods.getValues();
        return !!activeTabNotifications.find((item) => values?.[item]?.['p']);
    }, [user, activeTabNotifications, formMethods.watch()]);

    useEffect(() => {
        if (user) {
            const result: Record<string, TNotificationConfig> = {};
            Object.keys(user?.notifications).forEach((item) => {
                result[item] = user.notifications[item][Object.keys(user.notifications[item])[0]];
            });
            formMethods.reset(result, {
                keepDirty: false
            });
            setIsDisabled(true);
        }
    }, [user]);

    useEffect(() => {
        setIsDisabled(!formMethods.formState.isDirty);
    }, [formMethods.watch()]);

    const { handleRequest: handleUpdateNotifications, isLoading: isUpdateNotificationsLoading } =
        useApiMutation({
            method: () => {
                const values = formMethods.getValues();
                const result: TNotificationSettings = {};
                Object.keys(values || {}).forEach((group) => {
                    const localResult: Record<string, TNotificationConfig> = {};
                    Object.keys(user?.notifications?.[group] || {})?.forEach((item) => {
                        localResult[item] = values[group];
                    });
                    result[group] = localResult;
                });
                return apiUserService.updateNotificationSettings(result);
            },
            onSuccess: () => {
                requestSuccessLocale('update_notifications_settings');
                refetchUser();
            },
            onError: requestErrorLocale
        });

    const bulkNotificationsUpdate = (type: 'e' | 's' | 'p') => {
        const activeStatuses = {
            s: isSmsActive,
            e: isEmailActive,
            p: isPushActive
        };
        const values = formMethods.getValues();
        const activeTabValues: TNotificationSettings = {};
        const isAllActive = activeStatuses[type];
        activeTabNotifications.forEach((key) => {
            activeTabValues[key] = {
                ...values?.[key],
                [type]: !isAllActive
            };
        });
        Object.keys(activeTabValues).forEach((key) => {
            formMethods.setValue(`${key}.${type}`, !isAllActive, { shouldDirty: true });
        });
    };

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <div className={styles.header}>
                <Button
                    className={styles.back}
                    leftIcon="arrow_left"
                    variant="text-gray"
                    onClick={() => handleBack(routes.settings)}>
                    {submitLocale('back')}
                </Button>
                <Typography variant="h4">{t('title')}</Typography>
            </div>
            <div className={styles.title_wrapper}>
                <Typography variant="body3">
                    Manage how you want to receive your notification by categories and channels
                </Typography>
            </div>
            <div className={styles.tabs_wrapper}>
                <Tabs
                    onChange={(val) => setActiveTab(val)}
                    tabs={tabs.map((item) => ({ ...item, label: t(`tabs.${item.label}`) }))}
                    activeTab={activeTab}
                />
                <Button
                    variant="primary"
                    size="medium"
                    disabled={isDisabled}
                    isLoading={isUpdateNotificationsLoading}
                    onClick={handleUpdateNotifications}>
                    {submitLocale('save_changes')}
                </Button>
            </div>
            <FormProvider {...formMethods}>
                <div className={styles.content}>
                    <div className={styles.row}>
                        <div className={styles.header}>
                            <Typography variant="body3" weight="semibold">
                                Notifications
                            </Typography>
                            <div className={styles.switches_wrapper}>
                                <div className={styles.switch_wrapper}>
                                    <Typography variant="body3">{t('email')}</Typography>{' '}
                                    <Switch
                                        checked={isEmailActive}
                                        onClick={() => bulkNotificationsUpdate('e')}
                                    />
                                </div>
                                <div className={styles.switch_wrapper}>
                                    <Typography variant="body3">{t('text')}</Typography>{' '}
                                    <Switch
                                        checked={isSmsActive}
                                        onClick={() => bulkNotificationsUpdate('s')}
                                    />
                                </div>
                            </div>
                        </div>
                        {activeTabNotifications.map((item) => {
                            return (
                                <div className={styles.row} key={item}>
                                    <Typography
                                        className={styles.notification_name}
                                        variant="body3">
                                        {t(`notification_strings.${item}`, {
                                            defaultValue: humanizeNotificationGroupKey(item)
                                        })}
                                    </Typography>
                                    <div className={styles.switches_wrapper}>
                                        <FormField<boolean>
                                            name={`${item}.e`}
                                            renderComponent={({ value, onChange }) => {
                                                return (
                                                    <div className={styles.switch_wrapper}>
                                                        <Checkbox
                                                            checked={value}
                                                            onClick={() => onChange(!value)}
                                                        />
                                                    </div>
                                                );
                                            }}
                                        />
                                        <FormField<boolean>
                                            name={`${item}.s`}
                                            renderComponent={({ value, onChange }) => (
                                                <div className={styles.switch_wrapper}>
                                                    <Checkbox
                                                        checked={value}
                                                        onClick={() => onChange(!value)}
                                                    />
                                                </div>
                                            )}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </FormProvider>
        </motion.div>
    );
};

export default SettingsNotificationsLayout;
